import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import { getTopic, TOPICS } from "@/lib/topics";
import TopicNav from "@/components/site/TopicNav";
import LuxuryResidentialVisual from "@/components/visuals/LuxuryResidentialVisual";
import CommercialPropertyVisual from "@/components/visuals/CommercialPropertyVisual";
import LegalDocumentationVisual from "@/components/visuals/LegalDocumentationVisual";
import HoaCondominiumVisual from "@/components/visuals/HoaCondominiumVisual";
import PropertyInspectionVisual from "@/components/visuals/PropertyInspectionVisual";
import ClaimsReportsVisual from "@/components/visuals/ClaimsReportsVisual";
import BusinessOperationsVisual from "@/components/visuals/BusinessOperationsVisual";

const VISUAL_MAP = {
  luxury: LuxuryResidentialVisual,
  commercial: CommercialPropertyVisual,
  legal: LegalDocumentationVisual,
  hoa: HoaCondominiumVisual,
  inspection: PropertyInspectionVisual,
  reports: ClaimsReportsVisual,
  operations: BusinessOperationsVisual,
};

export default function TopicPage() {
  const { slug } = useParams();
  const topic = getTopic(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!topic) return <Navigate to="/" replace />;

  const Visual = VISUAL_MAP[topic.visual];

  return (
    <div className="btr-page-enter bg-white text-[#111114]" data-testid={`topic-page-${slug}`}>
      {/* Hero */}
      <section className="relative pt-24 pb-14 sm:pt-32 sm:pb-20 md:pt-40 md:pb-28 overflow-hidden bg-gradient-to-b from-[#f7f7f5] via-white to-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(162,2,11,0.08),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(0,0,0,0.04),transparent_55%)]" />
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#a2020b]/40 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[11px] font-display tracking-[0.2em] uppercase text-zinc-500 hover:text-[#a2020b] transition-colors"
            data-testid="back-to-services"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Services
          </Link>

          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center mt-8">
            <div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-display font-extrabold tracking-[0.3em] uppercase text-[#a2020b]">
                  {topic.number} / {String(TOPICS.length).padStart(2, "0")}
                </span>
                <span className="h-[1px] flex-1 max-w-16 bg-[#a2020b]/30" />
                <span className="text-[11px] font-display tracking-[0.2em] uppercase text-zinc-500">
                  Risk Intelligence
                </span>
              </div>

              <h1 className="font-display font-extrabold text-[#111114] text-[2.6rem] leading-[1] sm:text-6xl lg:text-7xl sm:leading-[0.95] mt-6 tracking-tight">
                {topic.title.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-[#a2020b]">
                  {topic.title.split(" ").slice(-1)}
                </span>
              </h1>

              <p className="font-body text-zinc-700 text-base sm:text-xl mt-6 sm:mt-7 leading-relaxed max-w-xl">
                {topic.lede}
              </p>

              <div className="flex flex-wrap gap-4 mt-9">
                <Link to="/contact" className="btr-btn-primary" data-testid="topic-cta-primary">
                  <ShieldCheck className="w-4 h-4" />
                  Schedule Consultation
                </Link>
                <Link to="/services" className="btr-btn-ghost !text-[#111114] !border-[#111114]/20 hover:!border-[#a2020b]" data-testid="topic-cta-secondary">
                  Explore All Services
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Visual */}
            <div className="btr-reveal is-visible">{Visual && <Visual />}</div>
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="relative py-16 sm:py-20 md:py-28 border-t border-zinc-200">
        <div className="max-w-5xl mx-auto px-5 sm:px-6">
          <span className="btr-badge" style={{ color: "#a2020b" }}>
            <span className="dot" />
            Why It Matters
          </span>
          <h2 className="font-display font-extrabold text-[#111114] text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.05] tracking-tight">
            {topic.sections.why.title}
          </h2>
          <p className="font-body text-zinc-700 text-base sm:text-lg mt-6 leading-relaxed max-w-3xl">
            {topic.sections.why.body}
          </p>
        </div>
      </section>

      {/* What we document */}
      <section className="relative py-20 md:py-28 bg-[#f5f5f3] border-t border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(162,2,11,0.06),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-[1fr_1.2fr] gap-10">
            <div>
              <span className="btr-badge">
                <span className="dot" />
                Scope of Work
              </span>
              <h2 className="font-display font-extrabold text-[#111114] text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.05] tracking-tight">
                {topic.sections.documents.title}
              </h2>
              <p className="font-body text-zinc-700 text-base mt-6 leading-relaxed">
                Every engagement is executed with field-documented rigor and a chain-of-evidence format designed for
                decision-makers, adjusters, and courts.
              </p>
            </div>

            <ul className="grid sm:grid-cols-2 gap-3">
              {topic.sections.documents.items.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 bg-white rounded-md p-4 border border-zinc-200 hover:border-[#a2020b]/50 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.03)]"
                >
                  <CheckCircle2 className="w-4.5 h-4.5 text-[#a2020b] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
                  <span className="font-body text-sm text-zinc-800 leading-snug">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-16 sm:py-20 md:py-28 bg-white border-t border-zinc-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-6">
          <span className="btr-badge">
            <span className="dot" />
            Benefits
          </span>
          <h2 className="font-display font-extrabold text-[#111114] text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.05] tracking-tight">
            {topic.sections.benefits.title}
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {topic.sections.benefits.items.map((b, i) => (
              <div
                key={b.label}
                className="relative bg-white border border-zinc-200 rounded-lg p-6 hover:border-[#a2020b]/60 hover:shadow-[0_14px_40px_-20px_rgba(162,2,11,0.45)] transition-all group"
              >
                <div className="absolute top-0 left-0 h-[2px] w-0 bg-[#a2020b] transition-all duration-500 group-hover:w-full" />
                <div className="text-[10px] font-display tracking-[0.3em] uppercase text-[#a2020b]">0{i + 1}</div>
                <div className="font-display font-bold text-[#111114] text-lg mt-2">{b.label}</div>
                <div className="font-body text-zinc-600 text-sm mt-2 leading-relaxed">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inline CTA band */}
      <section className="relative py-16 sm:py-20 md:py-28 bg-[#f5f5f3] border-t border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(162,2,11,0.1),transparent_55%)]" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-6 text-center">
          <h2 className="font-display font-extrabold text-[#111114] text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight" data-testid="topic-brand-line">
            Before the loss. Before the litigation. <span className="text-[#a2020b]">Before the risk.</span>
          </h2>
          <p className="font-body text-zinc-700 text-base sm:text-lg mt-5 max-w-2xl mx-auto">
            Every Before The Risk engagement begins with a confidential consultation.
            Tell us about the asset — we&rsquo;ll show you the exposure.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/contact" className="btr-btn-primary">
              <ShieldCheck className="w-4 h-4" />
              Schedule Consultation
            </Link>
            <Link to="/services" className="btr-btn-ghost !text-[#111114] !border-[#111114]/20 hover:!border-[#a2020b]">
              All Services <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Bottom nav to other topics */}
      <TopicNav currentSlug={slug} />
    </div>
  );
}
